/**
 * Bin Placement Grunnlag (Foundation) Module
 *
 * Displays items waiting for put-away in warehouse bins. Shows the staging/foundation
 * data (StcIntRelocation) for items that have been received but not yet placed in
 * their final storage locations.
 *
 * Features:
 * - List view of all items waiting for put-away (TransType = 1, LineStatus != 100)
 * - Filtering by warehouse, item number, status
 * - Search functionality
 * - Status color coding
 * - Batch/lot tracking display
 * - Expiry date warnings
 * - Responsive design with Tailwind CSS
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Database,
  Search,
  Filter,
  RefreshCw,
  Loader2,
  AlertCircle,
  Package,
  MapPin,
  Calendar,
  User,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { stcIntRelocationService } from '../../../../services/stcIntRelocationService';
import { StcIntRelocationDto } from '../../../../types/stcIntRelocation';
import { putAwayService } from '../../../../services/putAwayService';
import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';

// StcIntRelocation-status = StatusType 41 (Legacy/Venter/Tildelt ordre/Ferdig),
// TransType = StatusType 25 (Utlegg/Påfylling/Optimalisering). Master-basert (WS5, Model A).
const RELOCATION_STATUS_TYPE = 41;
const RELOCATION_TRANSTYPE_TYPE = 25;

const ITEMS_PER_PAGE = 50;

interface BinPlacementGrunnlagAppProps {
  onBack?: () => void;
}

export const BinPlacementGrunnlagApp: React.FC<BinPlacementGrunnlagAppProps> = ({ onBack }) => {
  const { user } = useAuth();

  // Data state
  const [grunnlagItems, setGrunnlagItems] = useState<StcIntRelocationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | ''>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Selection state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isCreatingPutAway, setIsCreatingPutAway] = useState(false);

  /**
   * Load grunnlag data on mount
   */
  useEffect(() => {
    loadGrunnlagData();
  }, []);

  /**
   * Debounce search input (300ms)
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  /**
   * Fetch grunnlag data from API
   */
  const loadGrunnlagData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await stcIntRelocationService.getGrunnlag(warehouseFilter || undefined);
      setGrunnlagItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load grunnlag data');
      console.error('Error loading grunnlag:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reload data when warehouse filter changes
   */
  useEffect(() => {
    if (!loading) {
      loadGrunnlagData();
    }
  }, [warehouseFilter]);

  // Status-dropdown: master-baserte, språk-bevisste opsjoner (ST41), kode-verdi. WS5/Model A.
  const statusOptions = useStatusOptions(RELOCATION_STATUS_TYPE);

  /**
   * Get unique warehouse values for the warehouse filter dropdown
   */
  const filterOptions = useMemo(() => {
    const warehouses = new Set<string>();
    grunnlagItems.forEach((item) => {
      if (item.warehouseID) warehouses.add(item.warehouseID);
    });
    return {
      warehouses: Array.from(warehouses).sort(),
    };
  }, [grunnlagItems]);

  /**
   * Apply filters to data
   */
  const filteredData = useMemo(() => {
    let filtered = grunnlagItems;

    // Search filter (item number, description, bin code, batch number, serial number, order numbers, or operator)
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.itemNo.toLowerCase().includes(searchLower) ||
          (item.description1 && item.description1.toLowerCase().includes(searchLower)) ||
          item.binCode.toLowerCase().includes(searchLower) ||
          (item.batchNo && item.batchNo.toLowerCase().includes(searchLower)) ||
          (item.serialNo && item.serialNo.toLowerCase().includes(searchLower)) ||
          (item.operatorID && item.operatorID.toLowerCase().includes(searchLower)) ||
          (item.orderNoERP && item.orderNoERP.toLowerCase().includes(searchLower)) ||
          (item.orderNo && item.orderNo.toString().includes(searchLower))
      );
    }

    // Status filter
    if (statusFilter !== '') {
      filtered = filtered.filter((item) => item.lineStatus === statusFilter);
    }

    return filtered;
  }, [grunnlagItems, debouncedSearchTerm, statusFilter]);

  /**
   * Paginate filtered data
   */
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  /**
   * Check if expiry date is soon or past
   */
  const getExpiryStatus = (expiryDate?: string): { text: string; color: string } | null => {
    if (!expiryDate) return null;

    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { text: 'Utgått', color: 'text-red-600' };
    } else if (daysUntilExpiry <= 7) {
      return { text: `${daysUntilExpiry} dager igjen`, color: 'text-orange-600' };
    } else if (daysUntilExpiry <= 30) {
      return { text: `${daysUntilExpiry} dager igjen`, color: 'text-yellow-600' };
    }

    return null;
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('no-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Format date only (no time)
   */
  const formatDateOnly = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('no-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  /**
   * Reset all filters
   */
  const resetFilters = () => {
    setSearchTerm('');
    setWarehouseFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  /**
   * Toggle selection of an item
   */
  const toggleSelection = (relID: string) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(relID)) {
      newSelection.delete(relID);
    } else {
      newSelection.add(relID);
    }
    setSelectedItems(newSelection);
  };

  /**
   * Toggle select all visible items
   */
  const toggleSelectAll = () => {
    if (selectedItems.size === paginatedData.length) {
      setSelectedItems(new Set());
    } else {
      const allRelIDs = paginatedData.map((item) => item.relID);
      setSelectedItems(new Set(allRelIDs));
    }
  };

  /**
   * Create put-away for selected items
   */
  const createPutAwayForSelected = async () => {
    if (selectedItems.size === 0) {
      alert('Ingen linjer valgt');
      return;
    }

    const selectedRelIDs = Array.from(selectedItems);
    const itemsToProcess = grunnlagItems.filter((item) => selectedRelIDs.includes(item.relID));

    if (
      !window.confirm(
        `Vil du opprette utlegg for ${selectedItems.size} valgte ${
          selectedItems.size === 1 ? 'linje' : 'linjer'
        }?\n\nVarer: ${itemsToProcess.map((i) => i.itemNo).join(', ')}`
      )
    ) {
      return;
    }

    setIsCreatingPutAway(true);

    try {
      // Call the new batch API endpoint
      const response = await putAwayService.createPutAwayFromRelocationBatch(selectedRelIDs);

      if (response.success) {
        // Success
        alert(
          `Utlegg opprettet!\n\n` +
          `Ordre nr: ${response.stcOrderNo || 'N/A'}\n` +
          `Prosessert: ${response.processedCount} linjer\n` +
          `Feilet: ${response.failedCount} linjer`
        );

        // Reload data to update the grid
        await loadGrunnlagData();
        setSelectedItems(new Set());
      } else {
        // Partial or complete failure
        let message = `Kunne ikke opprette utlegg:\n\n${response.errorMessage || 'Ukjent feil'}`;

        if (response.failures && response.failures.length > 0) {
          message += `\n\nFeilede linjer:\n`;
          response.failures.forEach((failure) => {
            message += `\n- ${failure.itemNo}: ${failure.errorMessage}`;
          });
        }

        alert(message);
      }
    } catch (err: any) {
      console.error('Error creating put-away:', err);
      alert(`Feil ved opprettelse av utlegg:\n\n${err.message || 'Ukjent feil'}`);
    } finally {
      setIsCreatingPutAway(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Laster grunnlag data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={loadGrunnlagData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header - Sticky */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BackButton onClick={onBack || (() => {})} />
            <Database className="w-8 h-8 text-green-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Grunnlag - Vareplassering</h1>
              <p className="text-sm text-gray-600">
                Viser varer som venter på plassering i lager
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Statistics - Inline */}
            <div className="flex items-center gap-6 mr-4">
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">Totalt</div>
                <div className="text-xl font-bold text-gray-800">{grunnlagItems.length}</div>
              </div>
              <div className="text-center">
                {/* ST41: 20 = Venter (tidligere feilmerket «Under arbeid») */}
                <div className="text-xs text-gray-500 mb-1">Venter</div>
                <div className="text-xl font-bold text-orange-600">
                  {grunnlagItems.filter((i) => i.lineStatus === 20).length}
                </div>
              </div>
              <div className="text-center">
                {/* ST41: 100 = Ferdig (tidligere feil kode 60 → talte alltid 0) */}
                <div className="text-xs text-gray-500 mb-1">Ferdig</div>
                <div className="text-xl font-bold text-green-600">
                  {grunnlagItems.filter((i) => i.lineStatus === 100).length}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">Filtrert</div>
                <div className="text-xl font-bold text-blue-600">{filteredData.length}</div>
              </div>
            </div>
            {/* Action buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={createPutAwayForSelected}
                disabled={selectedItems.size === 0 || isCreatingPutAway}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors font-medium ${
                  selectedItems.size === 0 || isCreatingPutAway
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isCreatingPutAway ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Oppretter...
                  </>
                ) : (
                  <>
                    <Package className="w-4 h-4 mr-2" />
                    Opprett utlegg ({selectedItems.size})
                  </>
                )}
              </button>
              <button
                onClick={loadGrunnlagData}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Oppdater
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters - Collapsible */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Søk varenr, beskrivelse, lokasjon, serienr, batch, ordrenr..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            />
          </div>

          {/* Warehouse filter */}
          <select
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          >
            <option value="">Alle lagre</option>
            {filterOptions.warehouses.map((wh) => (
              <option key={wh} value={wh}>
                Lager {wh}
              </option>
            ))}
          </select>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value === '' ? '' : parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          >
            <option value="">Alle statuser</option>
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Reset button */}
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
          >
            Nullstill filtre
          </button>
        </div>
      </div>

      {/* Data Table - Scrollable */}
      <div className="flex-1 overflow-auto bg-white">
        {paginatedData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Ingen varer funnet</p>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 border-b-2 border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.size === paginatedData.length && paginatedData.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Lagersted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Varenr.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Beskrivelse
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Mottakslok.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Foreslått lok.
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Antall
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Serienr.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Batch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Utløpsdato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Ordre (WMS)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Ordre (ERP)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Linje (ERP)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Dato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Operatør
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedData.map((item) => {
                    const expiryStatus = getExpiryStatus(item.expiryDate);
                    const isSelected = selectedItems.has(item.relID);
                    return (
                      <tr
                        key={item.relID}
                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelection(item.relID)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>

                        {/* Type (StatusType 25 — master-basert, språk-bevisst) */}
                        <td className="px-6 py-4">
                          <StatusBadge value={item.transType} statusType={RELOCATION_TRANSTYPE_TYPE} />
                        </td>

                        {/* Warehouse */}
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                            <span className="text-sm text-gray-800">{item.warehouseID}</span>
                          </div>
                        </td>

                        {/* Item Number */}
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Package className="w-4 h-4 text-gray-400 mr-1" />
                            <span className="text-sm font-medium text-gray-800">{item.itemNo}</span>
                          </div>
                        </td>

                        {/* Description */}
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-700">
                            {item.description1 || '-'}
                          </span>
                        </td>

                        {/* Bin Code (receiving location) */}
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-800 font-mono">{item.binCode}</span>
                        </td>

                        {/* Suggested Location (from ERP/POrderLine.StdLocationName) */}
                        <td className="px-6 py-4">
                          {item.stdLocationName ? (
                            <span className="text-sm text-green-700 font-mono font-semibold">{item.stdLocationName}</span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>

                        {/* Quantity */}
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-semibold text-gray-800">
                            {item.quantity.toFixed(2)}
                          </span>
                        </td>

                        {/* Serial Number */}
                        <td className="px-6 py-4">
                          {item.serialNo ? (
                            <span className="text-sm text-purple-700 font-mono font-medium">{item.serialNo}</span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>

                        {/* Batch Number */}
                        <td className="px-6 py-4">
                          {item.batchNo ? (
                            <span className="text-sm text-gray-700">{item.batchNo}</span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>

                        {/* Expiry Date */}
                        <td className="px-6 py-4">
                          {item.expiryDate ? (
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 text-gray-400 mr-1" />
                              <span className={`text-sm ${expiryStatus?.color || 'text-gray-700'}`}>
                                {formatDateOnly(item.expiryDate)}
                              </span>
                              {expiryStatus && (
                                <AlertTriangle className="w-4 h-4 ml-1 text-orange-500" />
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>

                        {/* Order Number (WMS) */}
                        <td className="px-6 py-4">
                          {item.orderNo ? (
                            <div className="flex items-center">
                              <FileText className="w-4 h-4 text-gray-400 mr-1" />
                              <span className="text-sm text-gray-700">{item.orderNo}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>

                        {/* Order Number (ERP) */}
                        <td className="px-6 py-4">
                          {item.orderNoERP ? (
                            <span className="text-sm text-gray-700 font-mono">{item.orderNoERP}</span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>

                        {/* Line Number (ERP) */}
                        <td className="px-6 py-4">
                          {item.lineNoERP ? (
                            <span className="text-sm text-gray-700 font-mono">{item.lineNoERP}</span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>

                        {/* Transaction Date */}
                        <td className="px-6 py-4">
                          <span className="text-xs text-gray-600">{formatDate(item.transDt)}</span>
                        </td>

                        {/* Status (StatusType 41 — master-basert, språk-bevisst) */}
                        <td className="px-6 py-4">
                          <StatusBadge value={item.lineStatus} statusType={RELOCATION_STATUS_TYPE} />
                        </td>

                        {/* Operator */}
                        <td className="px-6 py-4">
                          {item.operatorID ? (
                            <div className="flex items-center">
                              <User className="w-4 h-4 text-gray-400 mr-1" />
                              <span className="text-sm text-gray-700">{item.operatorID}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination - Fixed at bottom */}
            {totalPages > 1 && (
              <div className="border-t border-gray-200 bg-gray-50 px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Viser {(currentPage - 1) * ITEMS_PER_PAGE + 1} til{' '}
                    {Math.min(currentPage * ITEMS_PER_PAGE, filteredData.length)} av {filteredData.length} varer
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Forrige
                    </button>
                    <span className="px-4 py-2 text-sm font-medium text-gray-700">
                      Side {currentPage} av {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Neste
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
